/**
 * Edit your Reflections page: the cover (image or colour), the headline under your name, your own cover
 * text, which parts of your profile join it (summary, résumé, CV at a glance), and whether the page is
 * public. Backend: PATCH /reflections/page/, POST/DELETE /reflections/page/cover/.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Radio, RadioGroup, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { AddPhotoAlternate as CoverIcon } from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { LAB_COLORS, coverBackground } from 'components/collab/lab/labTheme';
import SlateEditor from './SlateEditor';

export default function ReflectionsPageDialog({ open, page, onClose, onSaved }) {
  const [headline, setHeadline] = useState('');
  const [about, setAbout] = useState('');
  const [color, setColor] = useState('');
  const [flags, setFlags] = useState({ show_summary: true, show_resume: true, show_cv: true });
  const [visibility, setVisibility] = useState('otable');
  const [cover, setCover] = useState(null);         // { file, url } | { url } | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !page) return;
    setHeadline(page.headline || ''); setAbout(page.about || ''); setColor(page.background_color || '');
    setFlags({ show_summary: page.settings?.show_summary ?? true, show_resume: page.settings?.show_resume ?? true, show_cv: page.settings?.show_cv ?? true });
    setVisibility(page.settings?.visibility || 'otable');
    setCover(page.cover_url ? { url: page.cover_url } : null);
    setError('');
  }, [open, page]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      await axiosService.patch('/reflections/page/', { headline, about, background_color: color, visibility, ...flags });
      if (cover?.file) {
        const fd = new FormData(); fd.append('cover', cover.file);
        await axiosService.post('/reflections/page/cover/', fd);
      } else if (!cover && page.cover_url) {
        await axiosService.delete('/reflections/page/cover/');
      }
      onSaved();
    } catch (e) {
      const d = e.response?.data || {};
      setError(d.cover || d.background_color || d.visibility || d.detail || 'The page could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const pick = (e) => {
    const file = e.target.files?.[0];
    if (file && /^image\/(png|jpe?g)$/.test(file.type)) setCover({ file, url: URL.createObjectURL(file) });
    else if (file) setError('A cover is a PNG or JPEG image.');
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit your Reflections page</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.25}>
          {error && <Alert severity="error">{String(error)}</Alert>}
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Cover</Typography>
            <Box sx={{ height: 120, borderRadius: 2, background: coverBackground({ cover_url: cover?.url, background_color: color }), mb: 1 }} />
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Button component="label" size="small" variant="outlined" startIcon={<CoverIcon />}>
                {cover ? 'Change image' : 'Add an image'}
                <input hidden type="file" accept="image/png,image/jpeg" onChange={pick} />
              </Button>
              {cover && <Button size="small" onClick={() => setCover(null)}>Remove image</Button>}
              <Box flex={1} />
              {LAB_COLORS.map((c) => (
                <Box key={c} onClick={() => setColor(c)} role="button" aria-label={`Colour ${c}`}
                  sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                    outline: color === c ? '2px solid' : 'none', outlineColor: 'text.primary', outlineOffset: 2 }} />
              ))}
            </Box>
          </Box>
          <TextField label="Headline" placeholder="A motto, or what your reflections are about" value={headline}
            onChange={(e) => setHeadline(e.target.value.slice(0, 200))} helperText={`Under your name on the cover · ${headline.length}/200`} />
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>About these reflections</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>The first thing on your page. Write as much or as little as you like.</Typography>
            <SlateEditor value={about} onChange={(v) => setAbout(v || '')} richText height={200}
              placeholder="Why you write, what you work on, what readers will find here…" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>From your profile</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>Kept up to date with your profile.</Typography>
            {[['show_summary', 'Summary (your bio)'], ['show_cv', 'CV at a glance (current positions and education)'], ['show_resume', 'Résumé']].map(([k, label]) => (
              <FormControlLabel key={k} sx={{ display: 'flex' }} label={label}
                control={<Switch checked={!!flags[k]} onChange={(e) => setFlags((f) => ({ ...f, [k]: e.target.checked }))} />} />
            ))}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Who can see the page</Typography>
            <RadioGroup value={visibility} onChange={(e) => setVisibility(e.target.value)}>
              <FormControlLabel value="otable" control={<Radio />} label="Everyone on Otable" />
              <FormControlLabel value="public" control={<Radio />}
                label="A public page anyone can open, with the reflections you mark “Anyone, on my public page”" />
            </RadioGroup>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={18} /> : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}
